import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import API from "../lib/api";
import { FileText, ArrowRight, Loader2, Sparkles, Building2, ShoppingBag, ShieldCheck, Store } from "lucide-react";

// ── Banking / Financial domain
const BANKING_REGULATORS = [
  "RBI",
  "SEBI",
  "IRDAI",
  "CBUAE",
  "DFSA",
  "ADGM",
  "FCA",
  "OCC",
  "FDIC",
  "ECB",
  "MAS",
  "HKMA",
  "OJK",
  "FSA",
];
const BANKING_REGULATORS_BY_COUNTRY = {
  india: ["RBI", "SEBI", "IRDAI", "FIU-IND"],
  uae: ["CBUAE", "DFSA", "ADGM", "SCA", "UAE FIU"],
  "united arab emirates": ["CBUAE", "DFSA", "ADGM", "SCA", "UAE FIU"],
  usa: ["Federal Reserve", "OCC", "FDIC", "SEC", "FINRA", "FinCEN"],
  "united states": ["Federal Reserve", "OCC", "FDIC", "SEC", "FINRA", "FinCEN"],
  uk: ["FCA", "PRA", "Bank of England"],
  "united kingdom": ["FCA", "PRA", "Bank of England"],
  eu: ["ECB", "EBA", "ESMA", "EIOPA"],
  singapore: ["MAS"],
  "hong kong": ["HKMA", "SFC", "IA"],
  indonesia: ["OJK", "Bank Indonesia", "PPATK"],
};
const BANKING_AUDIT_TYPES = ["Regulatory", "Process", "IT", "Risk-based", "Thematic", "Credit Risk", "AML/CFT"];
const BANKING_DEPARTMENTS = [
  "Retail Banking",
  "Corporate Banking",
  "Treasury",
  "Operations",
  "Compliance",
  "Risk Management",
  "Internal Audit",
  "IT & Information Security",
];

// ── Platform / E-Commerce domain
const PLATFORM_REGULATORS = ["FSSAI", "GST Council", "DPDP", "MCA21", "TRAI", "IT Act", "Consumer Protection Act"];
const PLATFORM_REGULATORS_BY_COUNTRY = {
  india: ["DPDP", "GST Council", "FSSAI", "Consumer Protection Act", "IT Act", "MCA21", "TRAI"],
  uae: ["UAE PDPL", "TDRA", "MOEC Consumer Protection", "FTA VAT", "DED"],
  "united arab emirates": ["UAE PDPL", "TDRA", "MOEC Consumer Protection", "FTA VAT", "DED"],
  usa: ["FTC", "State Privacy Laws", "PCI-DSS", "FDA Food Code"],
  "united states": ["FTC", "State Privacy Laws", "PCI-DSS", "FDA Food Code"],
  uk: ["ICO", "CMA", "FCA", "PCI-DSS"],
  "united kingdom": ["ICO", "CMA", "FCA", "PCI-DSS"],
  eu: ["GDPR", "DSA", "DMA", "PSD2", "PCI-DSS"],
  singapore: ["PDPC", "CCCS", "IRAS GST", "IMDA"],
  indonesia: ["PDP Law", "Kominfo", "BPKN", "DGT VAT"],
};
const PLATFORM_AUDIT_TYPES = ["Platform Integrity", "Partner Ecosystem", "Technology Governance", "Fraud & Trust", "Financial & Marketplace Controls", "Operational Platform Controls"];
const PLATFORM_DEPARTMENTS = [
  "Order Processing", "Delivery Fleet Operations", "Restaurant Network",
  "Coupon & Promotions", "Payout & Finance", "IT Operations & Security", "Customer Trust & Safety"
];

// ── Insurance domain
const INSURANCE_REGULATORS = ["OJK", "FSA", "IRDAI", "LIC", "GIC", "Lloyd's", "EIOPA", "NAIC", "FCA", "PRA", "SAMA"];
const INSURANCE_REGULATORS_BY_COUNTRY = {
  india: ["IRDAI", "RBI", "SEBI"],
  uae: ["CBUAE", "DFSA", "ADGM FSRA", "Insurance Authority"],
  "united arab emirates": ["CBUAE", "DFSA", "ADGM FSRA", "Insurance Authority"],
  usa: ["NAIC", "State Insurance Departments", "FINRA", "SEC"],
  "united states": ["NAIC", "State Insurance Departments", "FINRA", "SEC"],
  uk: ["FCA", "PRA", "Lloyd's"],
  "united kingdom": ["FCA", "PRA", "Lloyd's"],
  eu: ["EIOPA", "Solvency II", "GDPR"],
  singapore: ["MAS"],
  indonesia: ["OJK"],
};
const INSURANCE_AUDIT_TYPES = ["Claims Audit", "Underwriting Audit", "IT Governance"];
const INSURANCE_DEPARTMENTS = ["Claims", "Underwriting","IT"];

// ── Retail & Consumer domain (stores, omnichannel, malls, adjacent verticals)
const RETAIL_REGULATORS = [
  "DPDP",
  "GST Council",
  "Consumer Protection Act",
  "PCI-DSS",
  "UAE PDPL",
  "FSSAI",
  "MCA / Co. Law",
];
const RETAIL_REGULATORS_BY_COUNTRY = {
  india: ["DPDP", "GST Council", "Consumer Protection Act", "FSSAI", "Legal Metrology", "MCA / Co. Law", "PCI-DSS"],
  uae: ["UAE PDPL", "FTA VAT", "MOEC Consumer Protection", "DED", "PCI-DSS"],
  "united arab emirates": ["UAE PDPL", "FTA VAT", "MOEC Consumer Protection", "DED", "PCI-DSS"],
  usa: ["FTC", "State Privacy Laws", "PCI-DSS", "FDA Food Code", "State Sales Tax"],
  "united states": ["FTC", "State Privacy Laws", "PCI-DSS", "FDA Food Code", "State Sales Tax"],
  uk: ["ICO", "CMA", "HMRC VAT", "Food Standards Agency", "PCI-DSS"],
  "united kingdom": ["ICO", "CMA", "HMRC VAT", "Food Standards Agency", "PCI-DSS"],
  eu: ["GDPR", "Consumer Rights Directive", "VAT Directive", "PCI-DSS"],
  singapore: ["PDPC", "CCCS", "IRAS GST", "SFA", "PCI-DSS"],
  indonesia: ["PDP Law", "BPKN", "DGT VAT", "BPOM", "PCI-DSS"],
};
const RETAIL_AUDIT_TYPES = [
  "Store Operations & POS",
  "Inventory & Shrink",
  "Loss Prevention & Shrinkage Audit",
  "Omnichannel & E-commerce",
  "Revenue & Promotional Pricing",
  "Accounts & Financial Controls Audit",
  "Mall & Property / Lease",
  "Supply Chain & Procurement",
  "ITGC (Retail Systems)",
  "Hospitality & F&B",
  "Operational Intelligence",
  "Compliance"
];
const FINANCE_RISK_AUDIT_TYPE = "Finance & Risk";
const RETAIL_DEPARTMENTS = [
  "Store Operations",
  "Loss Prevention",
  "Finance & Accounts",
  "Merchandising & Planning",
  "Supply Chain & Logistics",
  "E-commerce & Digital",
  "Mall Management",
  "Hospitality & Leisure",
  "Health & Wellness (Retail)",
  "Corporate / Shared Services",
];

const DOMAIN_REGULATOR_OPTIONS = {
  banking: { fallback: BANKING_REGULATORS, byCountry: BANKING_REGULATORS_BY_COUNTRY },
  platform: { fallback: PLATFORM_REGULATORS, byCountry: PLATFORM_REGULATORS_BY_COUNTRY },
  insurance: { fallback: INSURANCE_REGULATORS, byCountry: INSURANCE_REGULATORS_BY_COUNTRY },
  retail: { fallback: RETAIL_REGULATORS, byCountry: RETAIL_REGULATORS_BY_COUNTRY },
};

const KNOWN_REGULATORS = new Set(
  Object.values(DOMAIN_REGULATOR_OPTIONS).flatMap(({ fallback, byCountry }) => [
    ...fallback,
    ...Object.values(byCountry).flat(),
  ])
);

const normalizeCountryKey = (country) => {
  const value = String(country || "").trim().toLowerCase();
  if (!value) return "";
  if (["in", "india", "bharat"].includes(value)) return "india";
  if (["uae", "u.a.e.", "united arab emirates"].includes(value)) return "uae";
  if (["us", "usa", "u.s.", "u.s.a.", "united states", "united states of america"].includes(value)) return "usa";
  if (["uk", "u.k.", "gb", "great britain", "united kingdom"].includes(value)) return "uk";
  if (["europe", "european union", "eu", "eea"].includes(value)) return "eu";
  return value;
};

const getRegulatorsForContext = (auditDomain, country) => {
  const config = DOMAIN_REGULATOR_OPTIONS[auditDomain] || DOMAIN_REGULATOR_OPTIONS.banking;
  const key = normalizeCountryKey(country);
  return config.byCountry[key] || config.fallback;
};

const customRegulatorsFrom = (regulators) =>
  (regulators || []).filter((reg) => reg && !KNOWN_REGULATORS.has(reg));

const filterRegulatorsForContext = (auditDomain, country, regulators) => {
  const allowed = new Set(getRegulatorsForContext(auditDomain, country));
  return (regulators || []).filter((reg) => allowed.has(reg) || !KNOWN_REGULATORS.has(reg));
};

const getDomainLabel = (auditDomain) =>
  auditDomain === "platform" ? "Platform" :
  auditDomain === "insurance" ? "Insurance" :
  auditDomain === "retail" ? "Retail & consumer" :
  "Banking";

function defaultAuditPeriodIsoDates() {
  const end = new Date();
  const start = new Date(end.getTime() - 365 * 24 * 60 * 60 * 1000);
  return {
    period_start: start.toISOString().slice(0, 10),
    period_end: end.toISOString().slice(0, 10),
  };
}

/** FastAPI validation errors expose `detail` as a string, object, or array of { loc, msg, type }. */
function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item?.msg) return item.msg;
        if (item?.loc && item?.msg) return `${item.loc.filter(Boolean).join(".")}: ${item.msg}`;
        try {
          return JSON.stringify(item);
        } catch {
          return String(item);
        }
      })
      .filter(Boolean)
      .join("; ");
  }
  if (typeof detail === "object" && typeof detail.message === "string") return detail.message;
  return null;
}

export default function Phase1Context() {
  const navigate = useNavigate();
  const { auditRun, advancePhase, refreshRun } = useAudit();
  const [saving, setSaving] = useState(false);
  const [customRegulator, setCustomRegulator] = useState("");
  const [customDepartment, setCustomDepartment] = useState("");
  const [domain, setDomain] = useState("banking"); // "banking" | "platform" | "insurance" | "retail"
  const [form, setForm] = useState({
    country: "", regulators: [], audit_type: "", department: "",
    risk_rating: "Medium", period_start: "", period_end: "", depth_level: "Standard"
  });

  useEffect(() => {
    if (!auditRun?.context) return;
    const ad = auditRun.context.audit_domain;
    const resolvedDomain = (ad === "banking" || ad === "platform" || ad === "insurance" || ad === "retail") ? ad : "banking";
    setForm({
      ...auditRun.context,
      regulators: filterRegulatorsForContext(
        resolvedDomain,
        auditRun.context.country,
        auditRun.context.regulators
      ),
    });
    if (ad === "banking" || ad === "platform" || ad === "insurance" || ad === "retail") {
      setDomain(ad);
    }
  }, [auditRun?.context]);

  const REGULATORS = getRegulatorsForContext(domain, form.country);

  const AUDIT_TYPES =
    domain === "platform" ? PLATFORM_AUDIT_TYPES :
    domain === "insurance" ? INSURANCE_AUDIT_TYPES :
    domain === "retail" ? RETAIL_AUDIT_TYPES :
    BANKING_AUDIT_TYPES;
  const AUDIT_TYPES_WITH_FINANCE_RISK = Array.from(new Set([...AUDIT_TYPES, FINANCE_RISK_AUDIT_TYPE]));

  const DEPARTMENTS =
    domain === "platform" ? PLATFORM_DEPARTMENTS :
    domain === "insurance" ? INSURANCE_DEPARTMENTS :
    domain === "retail" ? RETAIL_DEPARTMENTS :
    BANKING_DEPARTMENTS;

  const ALL_REGULATORS = Array.from(
    new Set([...(REGULATORS || []), ...customRegulatorsFrom(form.regulators)])
  );

  const suggestedRegulatorsFor = (newDomain, country, currentRegulators = []) =>
    Array.from(new Set([
      ...getRegulatorsForContext(newDomain, country),
      ...customRegulatorsFrom(currentRegulators),
    ]));

  const handleDomainSwitch = (newDomain) => {
    setDomain(newDomain);
    setForm(prev => ({
      ...prev,
      audit_type: "",
      regulators: suggestedRegulatorsFor(newDomain, prev.country, prev.regulators),
    }));
  };

  const handleCountryChange = (value) => {
    setForm(prev => ({
      ...prev,
      country: value,
      regulators: suggestedRegulatorsFor(domain, value, prev.regulators),
    }));
  };

  const toggleRegulator = (reg) => {
    setForm(prev => ({
      ...prev,
      regulators: prev.regulators.includes(reg)
        ? prev.regulators.filter(r => r !== reg)
        : [...prev.regulators, reg]
    }));
  };

  const addCustomRegulator = () => {
    const value = (customRegulator || "").trim();
    if (!value) return;
    setForm((prev) => ({
      ...prev,
      regulators: prev.regulators.includes(value) ? prev.regulators : [...prev.regulators, value],
    }));
    setCustomRegulator("");
  };

  const addCustomDepartment = () => {
    const value = (customDepartment || "").trim();
    if (!value) return;
    setForm((prev) => ({ ...prev, department: value }));
    setCustomDepartment("");
  };

  const handleSave = async () => {
    if (!auditRun) { toast.error("Create an audit run first"); return; }

    let period_start = (form.period_start || "").trim();
    let period_end = (form.period_end || "").trim();

    if (!period_start && !period_end) {
      const d = defaultAuditPeriodIsoDates();
      period_start = d.period_start;
      period_end = d.period_end;
      setForm((p) => ({ ...p, period_start, period_end }));
      toast.info("Audit period was empty — defaulted to the last 12 months. Adjust the dates if needed.");
    } else if (!period_start || !period_end) {
      toast.error("Set both period start and end, or clear both to use a default 12‑month range.");
      return;
    }

    if (period_end < period_start) {
      toast.error("Period end must be on or after period start.");
      return;
    }

    if (!(form.country || "").trim()) {
      toast.error("Country / jurisdiction is required.");
      return;
    }
    if (!(form.department || "").trim()) {
      toast.error("Department under audit is required.");
      return;
    }
    if (!(form.audit_type || "").trim()) {
      toast.error("Audit type is required.");
      return;
    }
    const effectiveRegulators = filterRegulatorsForContext(domain, form.country, form.regulators);
    if (!Array.isArray(effectiveRegulators) || effectiveRegulators.length === 0) {
      toast.error("Select at least one regulator or framework.");
      return;
    }
    if (!(form.depth_level || "").trim()) {
      toast.error("Depth level is required.");
      return;
    }

    const payload = {
      ...form,
      country: (form.country || "").trim(),
      department: (form.department || "").trim(),
      regulators: effectiveRegulators,
      period_start,
      period_end,
      audit_domain: domain,
    };

    setSaving(true);
    try {
      await API.put(`/audit-runs/${auditRun.id}/context`, payload);

      if ((payload.audit_type || "").trim().toLowerCase() === FINANCE_RISK_AUDIT_TYPE.toLowerCase()) {
        toast.success("Context saved. Redirecting to Finance & Risk Analytics...");
        window.location.href = "https://analytics.zentis.ai/finance";
        return;
      }

      await advancePhase(2);
      await refreshRun();
      toast.success("Context saved. Proceeding to Document Ingestion.");
      navigate("/phase/2");
    } catch (e) {
      const apiMsg = formatApiErrorDetail(e.response?.data?.detail);
      toast.error(apiMsg || e.message || "Failed to save context");
    }
    setSaving(false);
  };



  const depthRecommendation = form.risk_rating === "High" ? "Comprehensive" : form.risk_rating === "Medium" ? "Standard" : "Limited";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="phase-1">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Phase 1: Context Setup</h2>
          <p className="text-sm text-muted-foreground mt-1">Define the audit context, jurisdiction, and parameters.</p>
        </div>
        {auditRun && (
          <Button onClick={handleSave} disabled={saving} data-testid="save-context-top-btn" className="sm:shrink-0">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
            Save &amp; Continue
          </Button>
        )}
      </div>

      {!auditRun && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">Create a new audit run from the sidebar to begin.</p>
          </CardContent>
        </Card>
      )}

      {auditRun && (
        <Card>
          <CardHeader><CardTitle className="text-base">Audit Configuration</CardTitle></CardHeader>
          <CardContent className="space-y-5">

            {/* Domain Toggle */}
            <div>
              <Label className="text-xs mb-2 block">Audit Domain</Label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleDomainSwitch("banking")}
                  data-testid="domain-banking"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${domain === "banking"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50"
                    }`}
                >
                  <Building2 className="w-4 h-4" /> Banking &amp; Financial
                </button>
                <button
                  onClick={() => handleDomainSwitch("retail")}
                  data-testid="domain-retail"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${domain === "retail"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50"
                    }`}
                >
                  <Store className="w-4 h-4" /> Retail &amp; Consumer
                </button>
                <button
                  onClick={() => handleDomainSwitch("platform")}
                  data-testid="domain-platform"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${domain === "platform"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50"
                    }`}
                >
                  <ShoppingBag className="w-4 h-4" /> Platform &amp; E-Commerce
                </button>
                <button
                  onClick={() => handleDomainSwitch("insurance")}
                  data-testid="domain-insurance"
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${domain === "insurance"
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/50"
                    }`}
                >
                  <ShieldCheck className="w-4 h-4" /> Insurance
                </button>
              </div>
              {domain === "retail" && (
                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5">
                  Multi-format retail, omnichannel, mall operations, and adjacent consumer businesses — frameworks pre-selected for quick tuning.
                </p>
              )}
              {domain === "platform" && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5">
                  ⚡ Platform mode: FSSAI, GST, DPDP regulators pre-loaded. Suitable for Swiggy, Zomato, Amazon, Uber-style audits.
                </p>
              )}
              {domain === "insurance" && (
                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1.5">
                  {/* 🛡️ Insurance mode: IRDAI, Lloyds, Solvency II frameworks pre-loaded. Focused on claims, underwriting, and solvency. */}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Country / Jurisdiction</Label>
                <Input value={form.country} onChange={e => handleCountryChange(e.target.value)}
                  placeholder="e.g., India" data-testid="input-country" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Department Under Audit</Label>
                <Input value={form.department} onChange={e => setForm(p => ({ ...p, department: e.target.value }))}
                  placeholder={
                    domain === "retail" ? "e.g., Store Operations" :
                    domain === "platform" ? "e.g., Delivery Fleet Operations" :
                    domain === "insurance" ? "e.g., Claims Department" :
                    "e.g., Retail Banking"
                  }
                  data-testid="input-department" className="mt-1" />
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {DEPARTMENTS.map(d => (
                    <button key={d} type="button"
                      onClick={() => setForm(p => ({ ...p, department: d }))}
                      className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${form.department === d
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:border-primary/50"
                        }`}
                    >{d}</button>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={customDepartment}
                    onChange={(e) => setCustomDepartment(e.target.value)}
                    placeholder="Other department (custom)"
                    className="h-8 text-xs"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addCustomDepartment();
                      }
                    }}
                  />
                  <Button type="button" variant="outline" className="h-8 text-xs" onClick={addCustomDepartment}>
                    Add
                  </Button>
                </div>
              </div>
            </div>

            <div>
              <Label className="text-xs">
                Regulators / Frameworks (multi-select)
                <span className="ml-2 text-muted-foreground font-normal">
                  — {domain === "platform" ? "Platform" : domain === "insurance" ? "Insurance" : domain === "retail" ? "Retail & consumer" : "Banking"} frameworks
                </span>
              </Label>
              <p className="mt-1 text-xs text-muted-foreground">
                Showing regulators matched to {form.country || "the selected jurisdiction"} and {getDomainLabel(domain).toLowerCase()} audits.
              </p>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="regulator-select">
                {ALL_REGULATORS.map(r => (
                  <Badge key={r}
                    variant={form.regulators.includes(r) ? "default" : "outline"}
                    className="cursor-pointer text-xs"
                    onClick={() => toggleRegulator(r)}
                  >
                    {r}
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2 mt-2">
                <Input
                  value={customRegulator}
                  onChange={(e) => setCustomRegulator(e.target.value)}
                  placeholder="Other regulator/framework (custom)"
                  className="h-8 text-xs"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCustomRegulator();
                    }
                  }}
                />
                <Button type="button" variant="outline" className="h-8 text-xs" onClick={addCustomRegulator}>
                  Add
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Audit Type</Label>
                <Select value={form.audit_type} onValueChange={v => setForm(p => ({ ...p, audit_type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="select-audit-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {AUDIT_TYPES_WITH_FINANCE_RISK.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Risk Rating</Label>
                <Select value={form.risk_rating} onValueChange={v => setForm(p => ({ ...p, risk_rating: v, depth_level: v === "High" ? "Comprehensive" : v === "Medium" ? "Standard" : "Limited" }))}>
                  <SelectTrigger className="mt-1" data-testid="select-risk-rating"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Low">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Period Start</Label>
                <Input type="date" value={form.period_start} onChange={e => setForm(p => ({ ...p, period_start: e.target.value }))}
                  data-testid="input-period-start" className="mt-1 font-mono" />
              </div>
              <div>
                <Label className="text-xs">Period End</Label>
                <Input type="date" value={form.period_end} onChange={e => setForm(p => ({ ...p, period_end: e.target.value }))}
                  data-testid="input-period-end" className="mt-1 font-mono" />
              </div>
            </div>

            <div>
              <Label className="text-xs">Depth Level</Label>
              <Select value={form.depth_level} onValueChange={v => setForm(p => ({ ...p, depth_level: v }))}>
                <SelectTrigger className="mt-1" data-testid="select-depth"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Limited">Limited</SelectItem>
                  <SelectItem value="Standard">Standard</SelectItem>
                  <SelectItem value="Comprehensive">Comprehensive</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Recommendation: <span className="text-emerald-600 dark:text-emerald-400 font-medium">{depthRecommendation}</span> based on {form.risk_rating} risk rating.
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">

              <Button onClick={handleSave} disabled={saving} data-testid="save-context-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
                Save &amp; Continue
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}











//               <div className="rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-xs text-red-500">
//                 Fill in every Phase 1 field before moving to Document Ingestion.
//               </div>
//             )}

//             <div>
//               <h3 className="text-sm font-medium mb-1 flex items-center gap-2">
//                 <ShoppingBag className="w-4 h-4 text-primary" /> Platform &amp; E-Commerce Mode
//               </h3>
//               <p className="text-xs text-amber-600 dark:text-amber-400">
//                 ⚡ FSSAI, GST, DPDP regulators pre-loaded. Suitable for internal audits of platform operations.
//               </p>
//             </div>

//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <Label className="text-xs">Country / Jurisdiction</Label>
//                 <Input value={form.country} onChange={e => setForm(p => ({ ...p, country: e.target.value }))}
//                   placeholder="e.g., India" data-testid="input-country" className={fieldErrorClass(validationErrors.country)} />
//                 {renderFieldError(attemptedSave && validationErrors.country, "Country / jurisdiction is required.")}
//               </div>
//               <div>
//                 <Label className="text-xs">Department Under Audit</Label>
//                 <Input value={form.department} onChange={e => setForm(p => ({ ...p, department: e.target.value }))}
//                   placeholder="e.g., Delivery Fleet Operations"
//                   data-testid="input-department" className={fieldErrorClass(validationErrors.department)} />
//                 {renderFieldError(attemptedSave && validationErrors.department, "Department under audit is required.")}
//                 <div className="flex flex-wrap gap-1 mt-1.5">
//                   {PLATFORM_DEPARTMENTS.map(d => (
//                     <button key={d} type="button"
//                       onClick={() => setForm(p => ({ ...p, department: d }))}
//                       className={`text-xs px-2 py-0.5 rounded-full border transition-colors ${form.department === d
//                           ? "bg-primary text-primary-foreground border-primary"
//                           : "border-border text-muted-foreground hover:border-primary/50"
//                         }`}
//                     >{d}</button>
//                   ))}
//                 </div>
//               </div>
//             </div>

//             <div>
//               <Label className="text-xs">
//                 Regulators / Frameworks (multi-select)
//                 <span className="ml-2 text-muted-foreground font-normal">
//                   — Platform frameworks
//                 </span>
//               </Label>
//               <div className={`flex flex-wrap gap-1.5 mt-1 rounded-md ${attemptedSave && validationErrors.regulators ? "ring-1 ring-red-500/60 p-2" : ""}`} data-testid="regulator-select">
//                 {REGULATORS.map(r => (
//                   <Badge key={r}
//                     variant={form.regulators.includes(r) ? "default" : "outline"}
//                     className="cursor-pointer text-xs"
//                     onClick={() => toggleRegulator(r)}
//                   >
//                     {r}
//                   </Badge>
//                 ))}
//               </div>
//               {renderFieldError(attemptedSave && validationErrors.regulators, "Select at least one regulator or framework.")}
//             </div>

//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <Label className="text-xs">Audit Type</Label>
//                 <Select value={form.audit_type} onValueChange={v => setForm(p => ({ ...p, audit_type: v }))}>
//                   <SelectTrigger className={fieldErrorClass(validationErrors.audit_type)} data-testid="select-audit-type"><SelectValue placeholder="Select type" /></SelectTrigger>
//                   <SelectContent>
//                     {AUDIT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
//                   </SelectContent>
//                 </Select>
//                 {renderFieldError(attemptedSave && validationErrors.audit_type, "Audit type is required.")}
//               </div>
//               <div>
//                 <Label className="text-xs">Risk Rating</Label>
//                 <Select value={form.risk_rating} onValueChange={v => setForm(p => ({ ...p, risk_rating: v, depth_level: v === "High" ? "Comprehensive" : v === "Medium" ? "Standard" : "Limited" }))}>
//                   <SelectTrigger className={fieldErrorClass(validationErrors.risk_rating)} data-testid="select-risk-rating"><SelectValue /></SelectTrigger>
//                   <SelectContent>
//                     <SelectItem value="High">High</SelectItem>
//                     <SelectItem value="Medium">Medium</SelectItem>
//                     <SelectItem value="Low">Low</SelectItem>
//                   </SelectContent>
//                 </Select>
//                 {renderFieldError(attemptedSave && validationErrors.risk_rating, "Risk rating is required.")}
//               </div>
//             </div>

//             <div className="grid grid-cols-2 gap-4">
//               <div>
//                 <Label className="text-xs">Period Start</Label>
//                 <Input type="date" value={form.period_start} onChange={e => setForm(p => ({ ...p, period_start: e.target.value }))}
//                   data-testid="input-period-start" className={`${fieldErrorClass(validationErrors.period_start || validationErrors.period_order)} font-mono`} />
//                 {renderFieldError(attemptedSave && validationErrors.period_start, "Period start is required.")}
//               </div>
//               <div>
//                 <Label className="text-xs">Period End</Label>
//                 <Input type="date" value={form.period_end} onChange={e => setForm(p => ({ ...p, period_end: e.target.value }))}
//                   data-testid="input-period-end" className={`${fieldErrorClass(validationErrors.period_end || validationErrors.period_order)} font-mono`} />
//                 {renderFieldError(attemptedSave && validationErrors.period_end, "Period end is required.")}
//                 {renderFieldError(attemptedSave && validationErrors.period_order, "Period end must be on or after period start.")}
//               </div>
//             </div>

//             <div>
//               <Label className="text-xs">Depth Level</Label>
//               <Select value={form.depth_level} onValueChange={v => setForm(p => ({ ...p, depth_level: v }))}>
//                 <SelectTrigger className={fieldErrorClass(validationErrors.depth_level)} data-testid="select-depth"><SelectValue /></SelectTrigger>
//                 <SelectContent>
//                   <SelectItem value="Limited">Limited</SelectItem>
//                   <SelectItem value="Standard">Standard</SelectItem>
//                   <SelectItem value="Comprehensive">Comprehensive</SelectItem>
//                 </SelectContent>
//               </Select>
//               {renderFieldError(attemptedSave && validationErrors.depth_level, "Depth level is required.")}
//               <p className="text-xs text-muted-foreground mt-1">
//                 Recommendation: <span className="text-emerald-600 dark:text-emerald-400 font-medium">{depthRecommendation}</span> based on {form.risk_rating} risk rating.
//               </p>
//             </div>

//             <div className="flex justify-end gap-2 pt-2">
//               <Button variant="outline" onClick={handleRecommendAutofill} disabled={recommending || saving} data-testid="recommend-autofill-btn">
//                 {recommending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
//                 Recommend &amp; Autofill
//               </Button>
//               <Button onClick={handleSave} disabled={saving} data-testid="save-context-btn">
//                 {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowRight className="w-4 h-4 mr-2" />}
//                 Save &amp; Continue
//               </Button>
//             </div>
//           </CardContent>
//         </Card>
//       )}
//     </div>
//   );
// }
